import AddIcon from '@material-design-icons/svg/filled/add.svg?react';
import ContentCopyIcon from '@material-design-icons/svg/filled/content_copy.svg?react';
import DeleteIcon from '@material-design-icons/svg/filled/delete.svg?react';
import VisibilityOffIcon from '@material-design-icons/svg/filled/visibility_off.svg?react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useTRPC } from '../../hooks/useTRPC';
import { cn } from '../../lib/utils';
import {
  API_KEY_MANAGEMENT_PERMISSION_MESSAGE,
  isManageTeamPermissionError,
} from '../../utils/apiKeyAccess';
import { captureAndReportError } from '../../utils/errorReporting';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

type PortalTRPCClient = ReturnType<typeof useTRPC> & {
  developerApiKey: {
    listKeys: {
      query: (input: {
        publisherId: string;
        publisherType: 'organization' | 'individual';
      }) => Promise<any>;
    };
    createKey: {
      mutate: (input: {
        publisherId: string;
        publisherType: 'organization' | 'individual';
        label: string;
        permissions: Array<'ManageReleases' | 'SubmitToStore'>;
      }) => Promise<any>;
    };
    revokeKey: {
      mutate: (input: Record<string, unknown>) => Promise<any>;
    };
  };
};

const DEFAULT_API_KEY_PERMISSIONS = [
  'ManageReleases',
  'SubmitToStore',
] as const;

const PERMISSION_LABELS: Record<string, string> = {
  ManageReleases: 'Releases',
  ManageNFTs: 'NFTs',
  SubmitToStore: 'Store',
  ManageTeam: 'Team',
};

type DeveloperApiKey = {
  id: string;
  label: string;
  keyPrefix?: string;
  permissions?: string[];
  created?: string;
  lastUsedAt?: string | null;
  revokedAt?: string | null;
};

export type PublisherScope = {
  publisherId: string;
  publisherType: 'organization' | 'individual';
};

interface ApiKeyManagementPanelProps {
  publisherScope: PublisherScope;
  onPermissionDenied?: () => void;
}

function asPortalClient(client: ReturnType<typeof useTRPC>): PortalTRPCClient {
  return client as unknown as PortalTRPCClient;
}

function unwrapPortalResult<T>(result: unknown): T {
  if (
    result &&
    typeof result === 'object' &&
    '_tag' in result &&
    (result as { _tag?: string })._tag === 'Left'
  ) {
    const left = result as { left?: { message?: string } };
    throw new Error(left.left?.message || 'Request failed');
  }

  if (
    result &&
    typeof result === 'object' &&
    '_tag' in result &&
    (result as { _tag?: string })._tag === 'Right'
  ) {
    return (result as unknown as { right: T }).right;
  }

  return result as T;
}

function readKeys(result: unknown): DeveloperApiKey[] {
  if (Array.isArray(result)) {
    return result as DeveloperApiKey[];
  }

  if (result && typeof result === 'object') {
    const maybeKeys = (result as { keys?: unknown }).keys;
    if (Array.isArray(maybeKeys)) {
      return maybeKeys as DeveloperApiKey[];
    }
  }

  return [];
}

function readSecret(result: unknown): string | undefined {
  if (!result || typeof result !== 'object') {
    return undefined;
  }

  const secret = (result as { secret?: unknown }).secret;
  if (typeof secret === 'string' && secret.length > 0) {
    return secret;
  }

  const apiKey = (result as { apiKey?: unknown }).apiKey;
  if (typeof apiKey === 'string' && apiKey.length > 0) {
    return apiKey;
  }

  const value = (result as { value?: unknown }).value;
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }

  return undefined;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

function KeyRow({
  apiKey,
  onDelete,
}: {
  apiKey: DeveloperApiKey;
  onDelete: (key: DeveloperApiKey) => void;
}) {
  const permissions = apiKey.permissions ?? [];

  return (
    <div className="group grid grid-cols-[1fr_auto] items-start gap-4 px-5 py-4 transition-colors hover:bg-neutral-800/30 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.8fr)_auto]">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-neutral-100">
            {apiKey.label}
          </span>
          <span className="inline-block size-1.5 shrink-0 rounded-full bg-emerald-400/80" />
        </div>
        {apiKey.keyPrefix && (
          <code className="mt-1 block truncate font-mono text-xs text-neutral-400">
            {apiKey.keyPrefix}...
          </code>
        )}
      </div>

      <div className="hidden items-center gap-1.5 pt-0.5 md:flex">
        {permissions.map((perm) => (
          <span
            key={perm}
            className="rounded-md bg-neutral-800 px-2 py-0.5 text-[11px] font-medium text-neutral-300"
          >
            {PERMISSION_LABELS[perm] ?? perm}
          </span>
        ))}
        {permissions.length === 0 && (
          <span className="text-xs text-neutral-600">—</span>
        )}
      </div>

      <div className="hidden flex-col gap-0.5 md:flex">
        {apiKey.created ? (
          <span className="text-xs text-neutral-400">
            Created {formatDate(apiKey.created)}
          </span>
        ) : (
          <span className="text-xs text-neutral-600">—</span>
        )}
        <span className="text-[11px] text-neutral-500">
          {apiKey.lastUsedAt
            ? `Last used ${formatDate(apiKey.lastUsedAt)}`
            : 'Never used'}
        </span>
      </div>

      <div className="flex items-center justify-end self-center">
        <button
          type="button"
          onClick={() => onDelete(apiKey)}
          className="rounded-md p-1.5 text-neutral-600 transition-colors hover:bg-red-500/10 hover:text-red-400 md:opacity-0 md:group-hover:opacity-100"
          aria-label={`Delete ${apiKey.label}`}
        >
          <DeleteIcon className="size-4 fill-current" />
        </button>
      </div>

      {/* Mobile-only meta row */}
      <div className="col-span-2 flex flex-wrap items-center gap-2 md:hidden">
        {permissions.length > 0 && (
          <div className="flex gap-1">
            {permissions.map((perm) => (
              <span
                key={perm}
                className="rounded bg-neutral-800 px-1.5 py-0.5 text-[10px] font-medium text-neutral-400"
              >
                {PERMISSION_LABELS[perm] ?? perm}
              </span>
            ))}
          </div>
        )}
        <span className="text-[11px] text-neutral-500">
          {apiKey.created ? formatDate(apiKey.created) : ''}
          {apiKey.lastUsedAt
            ? ` · Used ${formatDate(apiKey.lastUsedAt)}`
            : apiKey.created
              ? ' · Never used'
              : ''}
        </span>
      </div>
    </div>
  );
}

function SecretBanner({
  secret,
  onDismiss,
}: {
  secret: string;
  onDismiss: () => void;
}) {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-800/80 bg-neutral-900/60">
      <div className="flex items-center justify-between gap-3 px-5 py-3">
        <div className="flex items-center gap-2.5">
          <VisibilityOffIcon className="size-4 shrink-0 fill-emerald-400" />
          <p className="text-sm font-medium text-neutral-100">
            Save your secret: it won't be shown again
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void handleCopy()}
            className="gap-1.5 text-neutral-200 hover:bg-neutral-800/80 hover:text-white"
          >
            <ContentCopyIcon className="size-3.5 fill-current" />
            Copy
          </Button>
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-md px-2.5 py-1 text-xs text-neutral-500 transition-colors hover:text-neutral-200"
          >
            Dismiss
          </button>
        </div>
      </div>
      <div className="border-t border-neutral-800/80 bg-black/30 px-5 py-3">
        <code className="block break-all font-mono text-[13px] leading-relaxed text-neutral-100">
          {secret}
        </code>
      </div>
    </div>
  );
}

export function ApiKeyManagementPanel({
  publisherScope,
  onPermissionDenied,
}: ApiKeyManagementPanelProps) {
  const trpc = asPortalClient(useTRPC());
  const [keys, setKeys] = useState<DeveloperApiKey[]>([]);
  const [label, setLabel] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [creatingSecret, setCreatingSecret] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<DeveloperApiKey | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeKeys = keys.filter((key) => !key.revokedAt);

  const handleManageTeamPermissionError = useCallback(
    (error: unknown) => {
      if (!isManageTeamPermissionError(error)) {
        return false;
      }

      toast.error('Team management permission required', {
        description: API_KEY_MANAGEMENT_PERMISSION_MESSAGE,
        duration: 6000,
      });
      onPermissionDenied?.();
      return true;
    },
    [onPermissionDenied],
  );

  const loadKeys = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = unwrapPortalResult<unknown>(
        await trpc.developerApiKey.listKeys.query(publisherScope),
      );
      setKeys(readKeys(result));
    } catch (error) {
      if (!handleManageTeamPermissionError(error)) {
        captureAndReportError('Failed to load API keys:', error);
        toast.error('Failed to load API keys');
      }
    } finally {
      setIsLoading(false);
    }
  }, [handleManageTeamPermissionError, publisherScope, trpc]);

  useEffect(() => {
    void loadKeys();
  }, [loadKeys]);

  const handleCreate = useCallback(async () => {
    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      toast.error('Enter a label for the API key');
      inputRef.current?.focus();
      return;
    }

    setIsCreating(true);
    try {
      const result = unwrapPortalResult<unknown>(
        await trpc.developerApiKey.createKey.mutate({
          publisherId: publisherScope.publisherId,
          publisherType: publisherScope.publisherType,
          label: trimmedLabel,
          permissions: [...DEFAULT_API_KEY_PERMISSIONS],
        }),
      );

      const secret = readSecret(result);
      if (secret) {
        setCreatingSecret(secret);
        try {
          await navigator.clipboard.writeText(secret);
          toast.success('Key created and copied to clipboard');
        } catch {
          toast.success('Key created');
        }
      } else {
        toast.success('Key created');
      }

      setLabel('');
      await loadKeys();
    } catch (error) {
      if (!handleManageTeamPermissionError(error)) {
        captureAndReportError('Failed to create API key:', error);
        toast.error('Failed to create API key');
      }
    } finally {
      setIsCreating(false);
    }
  }, [handleManageTeamPermissionError, label, loadKeys, publisherScope, trpc]);

  const handleDelete = useCallback(
    async (key: DeveloperApiKey) => {
      try {
        unwrapPortalResult<unknown>(
          await trpc.developerApiKey.revokeKey.mutate({ keyId: key.id }),
        );
        toast.success(`"${key.label}" deleted`);
        setDeletingKey(null);
        await loadKeys();
      } catch (error) {
        if (!handleManageTeamPermissionError(error)) {
          captureAndReportError('Failed to delete API key:', error);
          toast.error('Failed to delete API key');
        }
      }
    },
    [handleManageTeamPermissionError, loadKeys, trpc],
  );

  return (
    <>
      <div className="space-y-8">
        <div className="flex gap-3">
          <input
            ref={inputRef}
            aria-label="Key label"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !isCreating) {
                void handleCreate();
              }
            }}
            placeholder="Key label, e.g. CI pipeline"
            className="h-10 flex-1 rounded-lg border border-neutral-700/80 bg-neutral-900/50 px-4 text-sm text-white placeholder:text-neutral-500 focus:border-teal-500/60 focus:outline-none"
          />
          <Button
            onClick={() => void handleCreate()}
            disabled={isCreating}
            size="lg"
            className="gap-1.5 bg-teal-600 px-5 text-white hover:bg-teal-500"
          >
            <AddIcon className="size-4 fill-current" />
            {isCreating ? 'Creating...' : 'Create Key'}
          </Button>
        </div>

        {creatingSecret && (
          <SecretBanner
            secret={creatingSecret}
            onDismiss={() => setCreatingSecret(null)}
          />
        )}

        <div>
          <div className="mb-1 hidden grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.8fr)_auto] gap-4 px-5 md:grid">
            <span className="text-[11px] font-medium uppercase tracking-wider text-neutral-500">
              Name
            </span>
            <span className="text-[11px] font-medium uppercase tracking-wider text-neutral-500">
              Permissions
            </span>
            <span className="text-[11px] font-medium uppercase tracking-wider text-neutral-500">
              Activity
            </span>
            <span className="w-7" />
          </div>

          <div className="mb-1 flex items-center justify-between px-5 md:hidden">
            <span className="text-[11px] font-medium uppercase tracking-wider text-neutral-500">
              Active Keys
            </span>
            <span className="text-xs text-neutral-500">
              {isLoading ? '...' : activeKeys.length}
            </span>
          </div>

          <div
            className={cn(
              'overflow-hidden rounded-xl border border-neutral-800/80 divide-y divide-neutral-800/50',
              activeKeys.length === 0 && !isLoading && 'border-dashed',
            )}
          >
            {isLoading && activeKeys.length === 0 && (
              <div className="flex items-center justify-center py-16">
                <div className="size-5 animate-spin rounded-full border-2 border-neutral-700 border-t-neutral-400" />
              </div>
            )}

            {activeKeys.length === 0 && !isLoading && (
              <div className="flex flex-col items-center gap-2 py-16">
                <p className="text-sm text-neutral-400">No API keys yet</p>
                <p className="text-xs text-neutral-500">
                  Create your first key to start using the CLI
                </p>
              </div>
            )}

            {activeKeys.map((key) => (
              <KeyRow key={key.id} apiKey={key} onDelete={setDeletingKey} />
            ))}
          </div>
        </div>
      </div>

      <Dialog
        open={deletingKey !== null}
        onOpenChange={(open) => {
          if (!open) setDeletingKey(null);
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete API Key</DialogTitle>
            <DialogDescription className="text-neutral-300">
              <strong className="text-white">{deletingKey?.label}</strong> will
              be permanently revoked. Any integrations using this key will stop
              working immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <DialogClose asChild>
              <Button variant="outline" size="sm">
                Cancel
              </Button>
            </DialogClose>
            <Button
              size="sm"
              className="bg-red-600 text-white hover:bg-red-500"
              onClick={() => {
                if (deletingKey) void handleDelete(deletingKey);
              }}
            >
              Delete Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
